import { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams, Link } from 'react-router-dom';
import {
  ScanFace,
  Play,
  Users,
  HelpCircle,
  Tag,
  CheckCircle2,
  Loader2,
  ArrowLeft,
  ArrowRight,
  RefreshCw,
  Sparkles,
} from 'lucide-react';

import {
  useWizardStatus,
  useStartClustering,
  useWizardClusters,
  useAmbiguousPairs,
  useSubmitDecisions,
  useRecluster,
  useUpdatePerson,
} from '@/api/hooks/useFaces';
import { FaceComparisonView } from '@/components/faces/FaceComparisonView';
import { PersonNamingForm } from '@/components/faces/PersonNamingForm';
import { ClusterReviewStep } from '@/components/faces/ClusterReviewStep';
import type {
  MergeDecisionType,
  PersonUpdatePayload,
  WizardDecisionItem,
} from '@/api/types/face.types';

// ---------------------------------------------------------------------------
// Wizard Steps
// ---------------------------------------------------------------------------

type WizardStep = 'status' | 'clusters' | 'ambiguous' | 'naming' | 'summary';

const STEPS: WizardStep[] = ['status', 'clusters', 'ambiguous', 'naming', 'summary'];

function StepIndicator({
  currentStep,
  steps,
}: {
  currentStep: WizardStep;
  steps: WizardStep[];
}) {
  const { t } = useTranslation('faces');
  const currentIdx = steps.indexOf(currentStep);

  const stepIcons: Record<WizardStep, React.ReactNode> = {
    status: <Play className="h-4 w-4" />,
    clusters: <Users className="h-4 w-4" />,
    ambiguous: <HelpCircle className="h-4 w-4" />,
    naming: <Tag className="h-4 w-4" />,
    summary: <CheckCircle2 className="h-4 w-4" />,
  };

  return (
    <nav className="mb-8">
      <ol className="flex items-center justify-center gap-2">
        {steps.map((step, idx) => {
          const isActive = idx === currentIdx;
          const isCompleted = idx < currentIdx;

          return (
            <li key={step} className="flex items-center">
              {idx > 0 && (
                <div
                  className={`mx-2 h-px w-8 sm:w-12 ${
                    isCompleted
                      ? 'bg-primary-500'
                      : 'bg-gray-200 dark:bg-gray-700'
                  }`}
                />
              )}
              <div
                className={`flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-medium transition-colors ${
                  isActive
                    ? 'bg-primary-100 text-primary-700 dark:bg-primary-900/40 dark:text-primary-400'
                    : isCompleted
                      ? 'bg-primary-50 text-primary-600 dark:bg-primary-900/20 dark:text-primary-500'
                      : 'bg-gray-100 text-gray-500 dark:bg-gray-800 dark:text-gray-400'
                }`}
              >
                {stepIcons[step]}
                <span className="hidden sm:inline">
                  {t(`steps.${step}`)}
                </span>
              </div>
            </li>
          );
        })}
      </ol>
    </nav>
  );
}

// ---------------------------------------------------------------------------
// Step 1: Status / Start
// ---------------------------------------------------------------------------

function StatusStep({
  eventId,
  onNext,
}: {
  eventId: string;
  onNext: () => void;
}) {
  const { t } = useTranslation('faces');
  const { data: status, isLoading } = useWizardStatus(eventId);
  const startClustering = useStartClustering();

  const handleStart = () => {
    startClustering.mutate(eventId);
  };

  if (isLoading) {
    return (
      <div className="flex flex-col items-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-primary-500" />
        <p className="mt-3 text-sm text-gray-500">{t('common:status.loading')}</p>
      </div>
    );
  }

  const isClustered = status?.is_clustered ?? false;
  const needsReembedding = status?.needs_reembedding ?? false;
  const primaryCount = status?.primary_face_count ?? 0;

  return (
    <div className="mx-auto max-w-lg text-center">
      <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-primary-50 dark:bg-primary-900/30">
        <ScanFace className="h-8 w-8 text-primary-600 dark:text-primary-400" />
      </div>

      <h2 className="mb-2 text-lg font-semibold text-gray-900 dark:text-gray-100">
        {t('wizardTitle')}
      </h2>
      <p className="mb-6 text-sm text-gray-500 dark:text-gray-400">
        {t('wizardDescription')}
      </p>

      {/* Stats */}
      {status && (
        <div className="mb-6 grid grid-cols-3 gap-4">
          <div className="rounded-lg bg-gray-50 p-3 dark:bg-gray-800/50">
            <p className="text-xl font-bold text-gray-900 dark:text-gray-100">
              {status.primary_face_count}
            </p>
            <p className="text-xs text-gray-500">{t('primaryFaces')}</p>
          </div>
          <div className="rounded-lg bg-gray-50 p-3 dark:bg-gray-800/50">
            <p className="text-xl font-bold text-gray-900 dark:text-gray-100">
              {status.person_count}
            </p>
            <p className="text-xs text-gray-500">{t('personCount')}</p>
          </div>
          <div className="rounded-lg bg-gray-50 p-3 dark:bg-gray-800/50">
            <p className="text-xl font-bold text-gray-900 dark:text-gray-100">
              {status.ambiguous_remaining}
            </p>
            <p className="text-xs text-gray-500">{t('ambiguousRemaining')}</p>
          </div>
        </div>
      )}

      {/* Re-embedding notice */}
      {needsReembedding && !startClustering.isPending && (
        <div className="mb-4 rounded-lg bg-amber-50 p-3 text-xs text-amber-700 dark:bg-amber-900/20 dark:text-amber-400">
          {t('reembeddingNeeded')}
        </div>
      )}

      <div className="flex flex-col items-center gap-3">
        {!isClustered ? (
          <button
            onClick={handleStart}
            disabled={startClustering.isPending || primaryCount === 0}
            className="btn-primary flex items-center gap-2"
          >
            {startClustering.isPending ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : needsReembedding ? (
              <Sparkles className="h-4 w-4" />
            ) : (
              <Play className="h-4 w-4" />
            )}
            {startClustering.isPending
              ? t('preparingEmbeddings')
              : needsReembedding
                ? t('prepareAndCluster')
                : t('startClustering')}
          </button>
        ) : (
          <button onClick={onNext} className="btn-primary flex items-center gap-2">
            {t('reviewClusters')}
            <ArrowRight className="h-4 w-4" />
          </button>
        )}

        {primaryCount === 0 && (
          <p className="text-xs text-amber-600 dark:text-amber-400">
            {t('noFacesDetected')}
          </p>
        )}
      </div>
    </div>
  );
}


// ---------------------------------------------------------------------------
// Step 3: Resolve Ambiguous
// ---------------------------------------------------------------------------

function AmbiguousStep({
  eventId,
  onNext,
  onBack,
}: {
  eventId: string;
  onNext: () => void;
  onBack: () => void;
}) {
  const { t } = useTranslation('faces');
  const { data, isLoading, refetch } = useAmbiguousPairs(eventId, 10);
  const submitDecisions = useSubmitDecisions();
  const [currentPairIdx, setCurrentPairIdx] = useState(0);
  const [batchDecisions, setBatchDecisions] = useState<WizardDecisionItem[]>([]);

  const pairs = data?.pairs ?? [];
  const remaining = data?.remaining ?? 0;
  const currentPair = pairs[currentPairIdx];

  const handleDecide = useCallback(
    (decision: MergeDecisionType) => {
      if (!currentPair) return;

      const item: WizardDecisionItem = {
        face_a_id: currentPair.face_a.id,
        face_b_id: currentPair.face_b.id,
        decision,
      };

      const newBatch = [...batchDecisions, item];
      setBatchDecisions(newBatch);

      if (currentPairIdx < pairs.length - 1) {
        setCurrentPairIdx((prev) => prev + 1);
      } else {
        // Submit batch and fetch next
        submitDecisions.mutate(
          { eventId, payload: { decisions: newBatch } },
          {
            onSuccess: () => {
              setBatchDecisions([]);
              setCurrentPairIdx(0);
              refetch();
            },
          },
        );
      }
    },
    [currentPair, currentPairIdx, pairs.length, batchDecisions, eventId, submitDecisions, refetch],
  );

  if (isLoading) {
    return (
      <div className="flex flex-col items-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-primary-500" />
      </div>
    );
  }

  return (
    <div>
      <div className="mb-6 text-center">
        <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
          {t('resolveAmbiguousTitle')}
        </h2>
        <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
          {t('resolveAmbiguousDescription', { remaining })}
        </p>
      </div>

      {/* Progress */}
      {pairs.length > 0 && (
        <div className="mb-6 text-center text-xs text-gray-500">
          {t('pairProgress', {
            current: currentPairIdx + 1,
            total: pairs.length,
          })}
        </div>
      )}

      {/* Comparison */}
      {currentPair ? (
        <FaceComparisonView
          pair={currentPair}
          onDecide={handleDecide}
          isSubmitting={submitDecisions.isPending}
        />
      ) : (
        <div className="py-8 text-center">
          <CheckCircle2 className="mx-auto mb-3 h-12 w-12 text-green-500" />
          <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
            {t('allPairsResolved')}
          </p>
          <p className="mt-1 text-xs text-gray-500">
            {t('proceedToNaming')}
          </p>
        </div>
      )}

      <div className="mt-8 flex items-center justify-between">
        <button
          onClick={onBack}
          className="flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-800"
        >
          <ArrowLeft className="h-4 w-4" />
          {t('common:actions.back')}
        </button>
        <button onClick={onNext} className="btn-primary flex items-center gap-2">
          {t('common:actions.next')}
          <ArrowRight className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Step 4: Name People
// ---------------------------------------------------------------------------

function NamingStep({
  eventId,
  onNext,
  onBack,
}: {
  eventId: string;
  onNext: () => void;
  onBack: () => void;
}) {
  const { t } = useTranslation('faces');
  const { data: clusters, isLoading } = useWizardClusters(eventId);
  const updatePerson = useUpdatePerson();

  const handleSave = (id: string, payload: PersonUpdatePayload) => {
    updatePerson.mutate({ id, payload });
  };

  if (isLoading) {
    return (
      <div className="flex flex-col items-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-primary-500" />
      </div>
    );
  }

  return (
    <div>
      <div className="mb-6 text-center">
        <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
          {t('namePeopleTitle')}
        </h2>
        <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
          {t('namePeopleDescription')}
        </p>
      </div>

      <PersonNamingForm
        clusters={clusters ?? []}
        onSave={handleSave}
        isSaving={updatePerson.isPending}
      />

      <div className="mt-8 flex items-center justify-between">
        <button
          onClick={onBack}
          className="flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-800"
        >
          <ArrowLeft className="h-4 w-4" />
          {t('common:actions.back')}
        </button>
        <button onClick={onNext} className="btn-primary flex items-center gap-2">
          {t('common:actions.next')}
          <ArrowRight className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Step 5: Summary
// ---------------------------------------------------------------------------

function SummaryStep({
  eventId,
  onBack,
}: {
  eventId: string;
  onBack: () => void;
}) {
  const { t } = useTranslation('faces');
  const { data: status } = useWizardStatus(eventId);
  const { data: clusters } = useWizardClusters(eventId);
  const reclusterMutation = useRecluster();

  const namedCount = clusters?.filter((c) => c.name).length ?? 0;
  const totalClusters = clusters?.length ?? 0;

  return (
    <div className="mx-auto max-w-lg">
      <div className="mb-6 text-center">
        <CheckCircle2 className="mx-auto mb-3 h-12 w-12 text-green-500" />
        <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
          {t('summaryTitle')}
        </h2>
        <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
          {t('summaryDescription')}
        </p>
      </div>

      {/* Summary stats */}
      <div className="mb-6 space-y-3">
        <div className="flex items-center justify-between rounded-lg bg-gray-50 px-4 py-3 dark:bg-gray-800/50">
          <span className="text-sm text-gray-600 dark:text-gray-400">
            {t('primaryFaces')}
          </span>
          <span className="font-semibold text-gray-900 dark:text-gray-100">
            {status?.primary_face_count ?? 0}
          </span>
        </div>
        <div className="flex items-center justify-between rounded-lg bg-gray-50 px-4 py-3 dark:bg-gray-800/50">
          <span className="text-sm text-gray-600 dark:text-gray-400">
            {t('peopleClusters')}
          </span>
          <span className="font-semibold text-gray-900 dark:text-gray-100">
            {totalClusters}
          </span>
        </div>
        <div className="flex items-center justify-between rounded-lg bg-gray-50 px-4 py-3 dark:bg-gray-800/50">
          <span className="text-sm text-gray-600 dark:text-gray-400">
            {t('namedPeople')}
          </span>
          <span className="font-semibold text-gray-900 dark:text-gray-100">
            {namedCount} / {totalClusters}
          </span>
        </div>
        <div className="flex items-center justify-between rounded-lg bg-gray-50 px-4 py-3 dark:bg-gray-800/50">
          <span className="text-sm text-gray-600 dark:text-gray-400">
            {t('decisionsResolved')}
          </span>
          <span className="font-semibold text-gray-900 dark:text-gray-100">
            {status?.ambiguous_resolved ?? 0} / {status?.ambiguous_total ?? 0}
          </span>
        </div>
      </div>

      {/* Actions */}
      <div className="flex flex-col gap-3">
        {(status?.ambiguous_resolved ?? 0) > 0 && (
          <button
            onClick={() => reclusterMutation.mutate(eventId)}
            disabled={reclusterMutation.isPending}
            className="flex w-full items-center justify-center gap-2 rounded-lg bg-primary-600 px-4 py-2.5 text-sm font-medium text-white transition-colors hover:bg-primary-700 disabled:opacity-50"
          >
            {reclusterMutation.isPending ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <RefreshCw className="h-4 w-4" />
            )}
            {t('applyAndRecluster')}
          </button>
        )}

        <Link
          to="/people"
          className="flex w-full items-center justify-center gap-2 rounded-lg bg-gray-100 px-4 py-2.5 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-200 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-700"
        >
          <Users className="h-4 w-4" />
          {t('viewPeopleDirectory')}
        </Link>
      </div>

      <div className="mt-6 flex items-center justify-start">
        <button
          onClick={onBack}
          className="flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-800"
        >
          <ArrowLeft className="h-4 w-4" />
          {t('common:actions.back')}
        </button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main Page
// ---------------------------------------------------------------------------

export function FaceWizardPage() {
  const { t } = useTranslation('faces');
  const [searchParams] = useSearchParams();
  const eventId = searchParams.get('event') || '';
  const [step, setStep] = useState<WizardStep>('status');

  const stepIdx = STEPS.indexOf(step);
  const goNext = () => {
    if (stepIdx < STEPS.length - 1) setStep(STEPS[stepIdx + 1]);
  };
  const goBack = () => {
    if (stepIdx > 0) setStep(STEPS[stepIdx - 1]);
  };

  if (!eventId) {
    return (
      <div className="page-container">
        <h1 className="page-title">{t('common:nav.faces')}</h1>
        <div className="mt-6">
          <div className="card mx-auto max-w-lg text-center">
            <ScanFace className="mx-auto mb-3 h-10 w-10 text-gray-400" />
            <p className="text-sm text-gray-500 dark:text-gray-400">
              {t('selectEventFirst')}
            </p>
            <Link to="/events" className="btn-primary mt-4 inline-block">
              {t('goToEvents')}
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="page-container">
      <h1 className="page-title">{t('common:nav.faces')}</h1>

      <StepIndicator currentStep={step} steps={STEPS} />

      <div className="mx-auto max-w-4xl">
        {step === 'status' && (
          <StatusStep eventId={eventId} onNext={goNext} />
        )}
        {step === 'clusters' && (
          <>
            <ClusterReviewStep eventId={eventId} />
            <div className="mt-8 flex items-center justify-between">
              <button
                onClick={goBack}
                className="flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-800"
              >
                <ArrowLeft className="h-4 w-4" />
                {t('common:actions.back')}
              </button>
              <button onClick={goNext} className="btn-primary flex items-center gap-2">
                {t('common:actions.next')}
                <ArrowRight className="h-4 w-4" />
              </button>
            </div>
          </>
        )}
        {step === 'ambiguous' && (
          <AmbiguousStep eventId={eventId} onNext={goNext} onBack={goBack} />
        )}
        {step === 'naming' && (
          <NamingStep eventId={eventId} onNext={goNext} onBack={goBack} />
        )}
        {step === 'summary' && (
          <SummaryStep eventId={eventId} onBack={goBack} />
        )}
      </div>
    </div>
  );
}
